import { useState } from "react";
import { toast } from "sonner";
import { Star, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

/**
 * Rating dialog for both sides after shift completion.
 * props: { open, onClose, shiftId, rateeName, onDone }
 */
export default function RateDialog({ open, onClose, shiftId, rateeName, onDone }) {
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [review, setReview] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const submit = async () => {
    if (!stars) return toast.error("Pick a star rating");
    setBusy(true);
    try {
      await api.post("/ratings", { shift_id: shiftId, stars, review });
      toast.success("Rating submitted — thanks!");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit rating");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/50 flex items-end md:items-center justify-center p-0 md:p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-t-3xl md:rounded-3xl border border-slate-200 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Rating</div>
            <div className="font-display text-lg font-bold">Rate {rateeName}</div>
          </div>
          <button onClick={onClose} data-testid="rating-close" className="p-2 rounded-full hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2 block">How was your experience?</Label>
            <div className="flex items-center gap-1.5" onMouseLeave={() => setHover(0)}>
              {[1, 2, 3, 4, 5].map(i => (
                <button
                  key={i}
                  type="button"
                  data-testid={`rating-star-${i}`}
                  onMouseEnter={() => setHover(i)}
                  onClick={() => setStars(i)}
                  className="p-1 rounded-full hover:bg-amber-50 transition-colors"
                  aria-label={`${i} star`}
                >
                  <Star className={"w-8 h-8 " + (i <= (hover || stars) ? "fill-amber-400 text-amber-400" : "text-slate-300")} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">Say more (optional)</Label>
            <Textarea data-testid="rating-review" value={review} onChange={e => setReview(e.target.value)} rows={3} placeholder="What stood out? Punctuality, communication, professionalism…" className="rounded-xl" />
          </div>
          <Button onClick={submit} disabled={busy || !stars} className="w-full h-12 rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="rating-submit">
            {busy ? "Submitting…" : "Submit rating"}
          </Button>
        </div>
      </div>
    </div>
  );
}
